/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonERP,
  ButtonFilterERP,
  ButtonPrimeReact,
  CalendarPrimeReact,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GetDevolucionesByFiltersParamsProps } from "api/Devolucion.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { InputNumber } from "primereact/inputnumber"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"

export const FilterMenu = ({
  sortingCriteria,
  skipCount,
  maxResultCount,
  serviceQuery,
  openCreateDevolucionModal,
  resetPagination,
}: {
  sortingCriteria?: SortingCriteria
  skipCount?: GetDevolucionesByFiltersParamsProps["SkipCount"]
  maxResultCount?: GetDevolucionesByFiltersParamsProps["MaxResultCount"]
  serviceQuery: Dispatch<SetStateAction<GetDevolucionesByFiltersParamsProps>>
  openCreateDevolucionModal?: () => void
  resetPagination?: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [menuOpen, setMenuOpen] = useState(false)
  const [amount, setAmount] = useState<Record<string, number | undefined>>({
    min: undefined,
    max: undefined,
  })
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const [keyword, setKeyword] = useState<string | undefined>()

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (
    queryParams: GetDevolucionesByFiltersParamsProps
  ): GetDevolucionesByFiltersParamsProps => {
    const remesaParams = {
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      FechaLowerLimit: queryParams.FechaLowerLimit,
      FechaUpperLimit: queryParams.FechaUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "CreationTime DESC",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(remesaParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    ImporteLowerLimit: amount?.min,
    ImporteUpperLimit: amount?.max,
    FechaLowerLimit: date?.min && backendDateFormat(new Date(date.min)),
    FechaUpperLimit: date?.max && backendDateFormat(new Date(date.max)),
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setAmount({ min: undefined, max: undefined })
    setDate({ min: undefined, max: undefined })
  }

  const appliedFiltersTags = [
    {
      label: `${t("nsAssets.filters.date.since")}:`,
      value: date?.min
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.min))
        : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("nsAssets.filters.date.until")}:`,
      value: date?.max
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.max))
        : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.min)
        : undefined,
      action: () => setAmount({ ...amount, min: 0 }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.max)
        : undefined,
      action: () => setAmount({ ...amount, max: 0 }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    skipCount,
    maxResultCount,
    sortingCriteria,
    appliedFilters?.length,
    date?.min,
    date?.max,
    amount?.min,
    amount?.max,
  ])

  return (
    <>
      <div className='!tw-px-6 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-center tw-pb-4 tw-pt-6 tw-shadow-3xl tw-rounded-t-xl tw-bg-white'>
        <p className='tw-whitespace-nowrap tw-text-text tw-my-auto tw-text-xl'>
          {t("devoluciones.title")}
        </p>
        <div className='tw-flex tw-w-full tw-justify-center lg:tw-justify-end'>
          <div className='!tw-w-fit tw-flex tw-items-center !tw-flex-row tw-justify-end tw-flex-wrap'>
            <div className='tw-grid tw-gap-1 sm:tw-gap-3 sm:tw-flex sm:tw-flex-row sm:tw-grid-cols-1 tw-items-center tw-w-fit'>
              <SearchInput
                placeholder={`${t("devoluciones.placeholderSearch")}`}
                onChange={(ev: string) => {
                  resetPagination?.()
                  setKeyword(ev)
                }}
                inputValue={keyword}
                className='tw-mb-1 sm:tw-mb-0 tw-h-10 tw-w-full xl:tw-w-72'
              />

              <ButtonFilterERP
                isActive={appliedFilters.length > 0}
                numberActive={appliedFilters?.length}
                onClick={openFilters}
                deleteFilter={handleResetFilters}
                filterButtonLabel={`${t("filtros.title")}`}
              />

              <ButtonERP
                customize={{
                  size: "md",
                  type: "secondary",
                }}
                label={`${t("devoluciones.addReturn")}`}
                onClick={openCreateDevolucionModal}
              />
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "575px": "100vw" }}
          className='!tw-m-0 !tw-h-full tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary hover:!tw-bg-primary-over tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-flex tw-flex-row tw-mb-8'>
            <div className='tw-flex tw-flex-col'>
              <label>
                {t("bancos.conciliacion.filterMenu.amountLabelSince")}
              </label>
              <InputNumber
                className='[&_input]:!tw-w-36 !tw-w-36'
                value={amount.min}
                placeholder='0,00 €'
                onValueChange={(e) => setAmount({ ...amount, min: e.value })}
                minFractionDigits={2}
                min={0}
                max={amount?.max ?? Number.MAX_SAFE_INTEGER}
                mode='currency'
                currency='EUR'
              />
            </div>
            <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
              -
            </div>
            <div className='tw-flex tw-flex-col'>
              <label>
                {t("bancos.conciliacion.filterMenu.amountLabelUntil")}
              </label>
              <InputNumber
                className='[&_input]:!tw-w-36 !tw-w-36'
                value={amount.max}
                placeholder='0,00 €'
                onValueChange={(e) => {
                  setAmount({ ...amount, max: e.value })
                }}
                minFractionDigits={2}
                min={amount?.min ?? 0}
                max={Number.MAX_SAFE_INTEGER}
                mode='currency'
                currency='EUR'
              />
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.date.titleOperation")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col mobile:tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col tw-flex-grow tw-mb-4 mobile:tw-mb-0'>
                <label>{t("nsAssets.filters.date.since")}</label>
                <CalendarPrimeReact
                  id='fechaDesde'
                  name='fechaDesde'
                  clearButtonClassName='!tw-hidden'
                  value={date?.min ? new Date(date.min) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      max:
                        date?.max && new Date(eventDate) > new Date(date?.max)
                          ? undefined
                          : date?.max,
                      min: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='tw-w-full'
                  baseZIndex={2302}
                  showIcon
                  maxDate={date?.max ? new Date(date.max) : undefined}
                />
              </div>
              <div className='tw-hidden mobile:tw-block tw-py-3 tw-px-3 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col tw-flex-grow'>
                <label>{t("nsAssets.filters.date.until")}</label>
                <CalendarPrimeReact
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={date?.max ? new Date(date.max) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      min:
                        date?.min && new Date(eventDate) < new Date(date?.min)
                          ? undefined
                          : date?.min,
                      max: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='tw-w-full'
                  baseZIndex={2302}
                  showIcon
                  minDate={date?.min ? new Date(date.min) : undefined}
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
